// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The plunger, the cabinet and the tilt (M3-26, 07 §3.6).
//
// Everything in this file is a sound the *player* makes rather than one the ball makes, and
// that changes what it is for. A bumper tells you where the ball is; a cabinet thud tells you
// the machine registered what you just did to it. The whole nudging mechanic is a conversation
// conducted through this file: you shove, the machine answers, the bob swings, and you decide
// whether to shove again.
//
// **The bob is the reason this exists.** 07 §3.6 is unusually direct about it: "Hearing the bob
// swing after a hard nudge and *not* touch is one of the finest moments in pinball, and it is
// worth every line of code it takes." A player at a real machine gets that through their hands;
// through a screen they get nothing at all unless the bob is audible. `TiltBob` has simulated a
// real damped pendulum since M2 — its position, its velocity and the ring it can hit are all
// there — and until now none of it reached a speaker. It is the single largest piece of
// information the simulation was computing and throwing away.
//
// So the bob gets a **continuous** voice, not just a contact ting: a quiet metallic ring whose
// level follows how close it is to the ring and whose pan follows where it is. That is the
// stereo arc 07 asks for, and it is what makes a near miss legible.

import {
  addNoiseBurst,
  addPartial,
  addPitchDrop,
  addSquare,
  Biquad,
  combInPlace,
  fadeOut,
  filterInPlace,
  loopSeamless,
  normalise,
  OnePole,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { PinkNoise } from './rolling'

// --- the plunger ------------------------------------------------------------------------------

/** Seconds of noise in the plunger's loop. No modulation, so any whole length loops. */
export const PLUNGER_LOOP_SECONDS = 1
const PLUNGER_SEAM_MS = 60

/**
 * The loop the plunger pull is made of: pink noise, and nothing else.
 *
 * Separate from the rolling bed's loop even though both are pink noise, because that one has
 * thirty hertz of amplitude modulation baked into it — the wood grain a ball crosses. A plunger
 * being drawn is a steady scrape against a guide, and borrowing the rolling loop would give it a
 * flutter it has no physical reason to have.
 */
export function renderPlungerLoop(sampleRate: number, noise: NoiseSource): Samples {
  const length = Math.max(2, Math.round(PLUNGER_LOOP_SECONDS * sampleRate))
  const seam = Math.min(length - 1, samplesFor(PLUNGER_SEAM_MS, sampleRate))
  const pink = new PinkNoise(noise)
  const raw = new Float32Array(length + seam)
  for (let i = 0; i < raw.length; i++) raw[i] = pink.next()
  const out = loopSeamless(raw, length, seam)
  normalise(out)
  return out
}

/** Where the pull's band sits at rest and at full tension, Hz (07 §3.6: "rising with tension"). */
export const PULL_BASE_HZ = 240
export const PULL_SPAN_HZ = 900
export const PULL_Q = 2.2
/** Level of the pull at full tension, dB. A scrape, well under everything. */
export const PULL_FULL_DB = -22
/** Below this the plunger is barely moving and there is nothing to hear. */
export const PULL_GATE = 0.02

export function pullCentreHz(tension: number): number {
  const t = tension < 0 ? 0 : tension > 1 ? 1 : tension
  return PULL_BASE_HZ + PULL_SPAN_HZ * t
}

/**
 * Pull level from tension, 0…1.
 *
 * Rising rather than constant, because the sound is the spring compressing: there is more of it
 * to hear the further back the rod goes. A flat level makes the plunger a switch with a hiss.
 */
export function pullLevel(tension: number): number {
  if (tension < PULL_GATE) return 0
  const t = tension > 1 ? 1 : tension
  return Math.pow(t, 0.7)
}

/** The spring's own pitch, Hz. A comb delay of this length is what makes the twang a spring. */
export const TWANG_HZ = 165
const TWANG_FEEDBACK = 0.86
const TWANG_DECAY_MS = 210
/** The rod letting go: a hard, brief scrape before the spring takes over. */
const RELEASE_NOISE_HZ = 1400
const RELEASE_NOISE_MS = 6
/** The creak under the pull: low, slow, and the reason it sounds like wood and steel. */
const CREAK_HZ = 90
const CREAK_DECAY_MS = 150
const CREAK_GAIN = 0.35

export function plungerReleaseLengthMs(): number {
  return TWANG_DECAY_MS * 1.15 + 8
}

/**
 * The plunger let go.
 *
 * An impulse through a comb, which is what a spring physically is — a delay line that reflects.
 * The comb turns six milliseconds of noise into a pitched *boing* at `TWANG_HZ`, darkening as it
 * dies because each pass round the loop loses its top end. Nothing else in the product sounds
 * like this, which is right: nothing else in a pinball machine is a spring being released.
 */
export function renderPlungerRelease(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(plungerReleaseLengthMs(), sampleRate))

  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(RELEASE_NOISE_HZ * pitchScale, 1.4, sampleRate),
    1,
    RELEASE_NOISE_MS,
  )
  addPartial(out, sampleRate, CREAK_HZ * pitchScale, CREAK_GAIN, CREAK_DECAY_MS)
  combInPlace(out, sampleRate / (TWANG_HZ * pitchScale), TWANG_FEEDBACK)

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- the cabinet ------------------------------------------------------------------------------

/** The body: a big plywood box struck from outside. Low, broad, and gone quickly. */
const THUD_FROM_HZ = 130
const THUD_TO_HZ = 62
const THUD_GLIDE_MS = 30
const THUD_DECAY_MS = 90
/** Everything loose inside it — the ball trough, the coin mech, the wiring loom. */
const RATTLE_HZ = 2100
const RATTLE_DECAY_MS = 130
const RATTLE_GAIN = 0.4
/** The cabinet is wood and it has no top end. */
const SHOVE_MUFFLE_HZ = 2600

export function cabinetShoveLengthMs(): number {
  return RATTLE_DECAY_MS * 1.15 + 8
}

/**
 * A shove.
 *
 * The thud is the box and the rattle is its contents, and the second half is what makes it a
 * *machine* being shoved rather than a drum being hit. A real cabinet is full of loose metal.
 */
export function renderCabinetShove(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(cabinetShoveLengthMs(), sampleRate))

  addPitchDrop(
    out,
    sampleRate,
    THUD_FROM_HZ * pitchScale,
    THUD_TO_HZ * pitchScale,
    THUD_GLIDE_MS,
    1,
    THUD_DECAY_MS,
  )
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(RATTLE_HZ * pitchScale, 1.1, sampleRate),
    RATTLE_GAIN,
    RATTLE_DECAY_MS,
  )
  filterInPlace(out, new OnePole(SHOVE_MUFFLE_HZ, sampleRate))

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

/** Nudge impulse, N·s, at which a shove is at full level. A full-strength nudge is about this. */
export const SHOVE_REFERENCE_IMPULSE = 26

export function shoveLevel(impulseX: number, impulseY: number): number {
  const magnitude = Math.sqrt(impulseX * impulseX + impulseY * impulseY)
  const raw = Math.min(magnitude / SHOVE_REFERENCE_IMPULSE, 1)
  return Math.pow(raw, 0.6)
}

// --- the tilt bob -----------------------------------------------------------------------------

/** The bob's fundamental, Hz (07 §3.6). A small steel plumb on a wire. */
export const BOB_HZ = 2400

/**
 * Cycles of `BOB_HZ` in the loop.
 *
 * A whole number, so the loop needs no cross-fade at all: a periodic signal whose length is an
 * exact multiple of its own period joins to itself perfectly. Every partial below is also an
 * exact multiple of the loop's own 20 Hz fundamental, for the same reason — one that is not
 * would beat against the loop point once a lap.
 */
const BOB_LOOP_CYCLES = 120

/**
 * The bob's partials, as multiples of the loop's fundamental rather than of `BOB_HZ`.
 *
 * A hanging plumb is a small metal body, so it is inharmonic like the chime bars — but the
 * free-bar ratios have to be snapped to the loop grid or the loop is not a loop. 120, 331 and
 * 648 are 2400 Hz, 6620 and 12960: the 2.76 ratio rounded to the nearest grid point, and 5.40
 * exactly on one.
 */
const BOB_PARTIALS: readonly (readonly [cycles: number, gain: number])[] = [
  [120, 1],
  [331, 0.28],
  [648, 0.1],
]

/**
 * The loop the bob sings on while it swings.
 *
 * Rendered at a constant amplitude and gated entirely by the runtime gain, which is what makes
 * it a *presence* rather than a note: a bob at rest is silent, a swinging one is a thin ring
 * that moves across the stereo field, and the closer it comes to the ring the more of it there
 * is. None of that is audible on a real machine, and all of it is information a player at a
 * real machine gets through their hands.
 */
export function renderBobLoop(sampleRate: number): Samples {
  const fundamental = BOB_HZ / BOB_LOOP_CYCLES
  const length = Math.max(2, Math.round(sampleRate / fundamental))
  const out = new Float32Array(length)
  for (const [cycles, gain] of BOB_PARTIALS) {
    // No decay: `addPartial`'s envelope must not move, or the loop has a step in it.
    addPartial(out, sampleRate, (cycles * sampleRate) / length, gain, Number.POSITIVE_INFINITY)
  }
  normalise(out)
  return out
}

/** Level of the bob's ring at the moment it touches, dB. Very quiet — it is a hint, not an event. */
export const BOB_FULL_DB = -30

/**
 * Bob level from how close it is to the ring, 0…1.
 *
 * `proximity` is 0 at rest and 1 at contact. Squared, so the ring is nearly silent through the
 * small swings that happen constantly and clearly audible through the one that is about to cost
 * a ball — which is the only distinction the sound has to make.
 */
export function bobLevel(proximity: number): number {
  const p = proximity < 0 ? 0 : proximity > 1 ? 1 : proximity
  return p * p
}

/** The ting: the plumb touching the ring. Brief, bright, and unmistakable. */
const TING_DECAY_MS = 110
const TING_TICK_HZ = 5200
const TING_TICK_MS = 1.5

export function bobTingLengthMs(): number {
  return TING_DECAY_MS * 1.15 + 8
}

export function renderBobTing(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(bobTingLengthMs(), sampleRate))

  addPartial(out, sampleRate, BOB_HZ * pitchScale, 1, TING_DECAY_MS)
  addPartial(out, sampleRate, BOB_HZ * 2.76 * pitchScale, 0.3, TING_DECAY_MS * 0.55)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(TING_TICK_HZ * pitchScale, 2, sampleRate),
    0.5,
    TING_TICK_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- the buzzers ------------------------------------------------------------------------------

/** 07 §3.6: "a two-tone buzzer, 220 / 180 Hz square with a hard gate". */
export const WARNING_HIGH_HZ = 220
export const WARNING_LOW_HZ = 180
/** How long each tone is held before the other takes over, ms. */
const WARNING_ALTERNATE_MS = 55
export const WARNING_LENGTH_MS = 440

/**
 * The tilt warning.
 *
 * A **square**, because the odd harmonics are the whole reason a buzzer is unpleasant enough to
 * work as a warning; a sine at the same pitch is a doorbell. The "hard gate" is the alternation
 * itself — each tone starts and stops abruptly rather than crossfading, which is what a
 * mechanical buzzer does and what makes it impossible to mistake for music.
 */
export function renderTiltWarning(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(WARNING_LENGTH_MS, sampleRate))
  const slot = samplesFor(WARNING_ALTERNATE_MS, sampleRate)
  for (let start = 0, i = 0; start < out.length; start += slot, i++) {
    addSquare(
      out,
      sampleRate,
      i % 2 === 0 ? WARNING_HIGH_HZ : WARNING_LOW_HZ,
      0.7,
      // Held flat across its slot: a decay inside a gate is a gate that does not sound hard.
      Number.POSITIVE_INFINITY,
      start,
      start + slot,
    )
  }
  normalise(out)
  fadeOut(out, sampleRate, 4)
  return out
}

/** The tilt itself: lower, longer, and with the mechanism rattling behind it. */
export const TILT_HZ = 92
export const TILT_LENGTH_MS = 1500
const TILT_RATTLE_HZ = 1600

/**
 * TILT.
 *
 * "A long harsh buzz with a noise rattle." Long enough to be a punishment rather than a beep —
 * the ball is over, the flippers are dead, and the machine should say so for as long as it takes
 * a player to notice. The rattle is the buzzer's own armature, which on a real machine is a
 * relay hammering against its stop for the duration.
 */
export function renderTiltBuzz(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(TILT_LENGTH_MS, sampleRate))
  addSquare(out, sampleRate, TILT_HZ, 0.8, Number.POSITIVE_INFINITY)
  addSquare(out, sampleRate, TILT_HZ * 1.5, 0.25, Number.POSITIVE_INFINITY)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(TILT_RATTLE_HZ, 1.3, sampleRate),
    0.3,
    TILT_LENGTH_MS,
  )
  normalise(out)
  fadeOut(out, sampleRate, 12)
  return out
}
